import { useState, useRef, useEffect } from 'react';
import { FaSearch, FaTimes, FaFilter, FaChevronDown } from 'react-icons/fa';
import { useDebounce } from '../../hooks/useDebounce';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import './SearchBar.css';

const SearchBar = ({
  onSearch,
  placeholder = 'Search products...',
  showFilters = true,
  className = '',
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [filters, setFilters] = useState({
    category: 'all',
    priceRange: 'all',
    sortBy: 'relevance',
  });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [loading, setLoading] = useState(false);

  const debouncedQuery = useDebounce(query, 300);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Mock categories - in real app, this would come from API
  const categories = [
    { id: 'all', name: 'All Categories' },
    { id: 'tops', name: 'Tops' },
    { id: 'bottoms', name: 'Bottoms' },
    { id: 'dresses', name: 'Dresses' },
    { id: 'accessories', name: 'Accessories' },
    { id: 'shoes', name: 'Shoes' },
  ];

  const priceRanges = [
    { id: 'all', name: 'All Prices' },
    { id: '0-50', name: 'Under $50' },
    { id: '50-100', name: '$50 - $100' },
    { id: '100-200', name: '$100 - $200' },
    { id: '200+', name: '$200+' },
  ];

  const sortOptions = [
    { id: 'relevance', name: 'Relevance' },
    { id: 'price-low', name: 'Price: Low to High' },
    { id: 'price-high', name: 'Price: High to Low' },
    { id: 'newest', name: 'Newest First' },
    { id: 'rating', name: 'Highest Rated' },
  ];

  // Fetch suggestions when debounced query changes
  useEffect(() => {
    if (debouncedQuery.length > 2) {
      fetchSuggestions(debouncedQuery);
    } else {
      setSuggestions([]);
    }
  }, [debouncedQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowFilterPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async searchQuery => {
    setLoading(true);
    try {
      // Mock API call - replace with actual API
      const mockSuggestions = [
        { id: 1, name: 'Summer Dress', category: 'dresses', image: '/assets/dress1.jpg' },
        { id: 2, name: 'Cotton T-Shirt', category: 'tops', image: '/assets/tshirt1.jpg' },
        { id: 3, name: 'Denim Jeans', category: 'bottoms', image: '/assets/jeans1.jpg' },
        { id: 4, name: 'Leather Jacket', category: 'tops', image: '/assets/jacket1.jpg' },
        { id: 5, name: 'Sneakers', category: 'shoes', image: '/assets/shoes1.jpg' },
      ].filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()));

      setSuggestions(mockSuggestions);
      setIsOpen(mockSuggestions.length > 0);
    } catch (error) {
      // Error will be handled by global error handler
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchQuery = query) => {
    if (searchQuery.trim()) {
      const searchParams = new URLSearchParams({
        q: searchQuery.trim(),
        ...filters,
      });
      navigate(`/shop?${searchParams.toString()}`);
      setIsOpen(false);
      onSearch?.(searchQuery, filters);
    }
  };

  const handleSuggestionClick = suggestion => {
    setQuery(suggestion.name);
    handleSearch(suggestion.name);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setShowFilterPanel(false);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setSuggestions([]);
    setIsOpen(false);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value,
    }));
  };

  return (
    <div ref={searchRef} className={`search-bar-container ${className}`}>
      <div className='search-input-wrapper'>
        {/* Search Icon - Left Position */}
        <div className='search-icon'>
          <FaSearch />
        </div>

        {/* Main Search Input */}
        <input
          type='text'
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className='search-input'
        />

        {/* Clear Button */}
        {query && (
          <button
            onClick={clearSearch}
            className='clear-search-btn'
            type='button'
            aria-label='Clear search'
          >
            <FaTimes />
          </button>
        )}

        {/* Filter Toggle Button */}
        {showFilters && (
          <button
            onClick={() => setShowFilterPanel(!showFilterPanel)}
            className={`filter-toggle-btn ${showFilterPanel ? 'active' : ''}`}
            type='button'
            aria-label='Toggle filters'
          >
            <FaFilter />
            <span className='filter-text'>Filters</span>
            <FaChevronDown className={`filter-arrow ${showFilterPanel ? 'rotated' : ''}`} />
          </button>
        )}

        {/* Search Button */}
        <Button
          onClick={() => handleSearch()}
          size='sm'
          className='search-submit-btn'
          disabled={!query.trim()}
        >
          Search
        </Button>
      </div>

      {/* Search Suggestions Dropdown */}
      {isOpen && suggestions.length > 0 && (
        <div className='search-suggestions'>
          {loading && (
            <div className='search-loading'>
              <div className='loading-spinner'></div>
              <span>Searching...</span>
            </div>
          )}

          {!loading &&
            suggestions.map(suggestion => (
              <button
                key={suggestion.id}
                onClick={() => handleSuggestionClick(suggestion)}
                className='suggestion-item'
              >
                <div className='suggestion-image'>
                  {suggestion.image && (
                    <img
                      src={suggestion.image}
                      alt={suggestion.name}
                      onError={e => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                </div>
                <div className='suggestion-content'>
                  <div className='suggestion-text'>{suggestion.name}</div>
                  <div className='suggestion-category'>{suggestion.category}</div>
                </div>
                <FaSearch className='suggestion-icon' />
              </button>
            ))}
        </div>
      )}

      {/* Filter Panel */}
      {showFilterPanel && (
        <div className='filter-panel'>
          <div className='filter-header'>
            <h3>Filters</h3>
            <button
              onClick={() => setShowFilterPanel(false)}
              className='filter-close-btn'
              aria-label='Close filters'
            >
              <FaTimes />
            </button>
          </div>

          <div className='filter-content'>
            {/* Category Filter */}
            <div className='filter-section'>
              <label className='filter-label'>Category</label>
              <select
                value={filters.category}
                onChange={e => handleFilterChange('category', e.target.value)}
                className='filter-select'
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Price Range Filter */}
            <div className='filter-section'>
              <label className='filter-label'>Price Range</label>
              <select
                value={filters.priceRange}
                onChange={e => handleFilterChange('priceRange', e.target.value)}
                className='filter-select'
              >
                {priceRanges.map(range => (
                  <option key={range.id} value={range.id}>
                    {range.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort By Filter */}
            <div className='filter-section'>
              <label className='filter-label'>Sort By</label>
              <select
                value={filters.sortBy}
                onChange={e => handleFilterChange('sortBy', e.target.value)}
                className='filter-select'
              >
                {sortOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Filter Actions */}
            <div className='filter-actions'>
              <button
                onClick={() => {
                  setFilters({
                    category: 'all',
                    priceRange: 'all',
                    sortBy: 'relevance',
                  });
                }}
                className='filter-btn secondary'
              >
                Reset
              </button>
              <button
                onClick={() => {
                  handleSearch();
                  setShowFilterPanel(false);
                }}
                className='filter-btn primary'
                disabled={!query.trim()}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
